/**
 * PMT Gateway Checkout Demo
 *
 * Minimal checkout example showing:
 * 1. Wallet connection
 * 2. Payment creation
 * 3. Direct payment execution
 */

import { PMTGatewayAssetHub, PaymentIntent } from '@pmt-gateway/asset-hub-sdk';
import { web3Enable, web3Accounts } from '@polkadot/extension-dapp';

// ============================================================================
// Global State
// ============================================================================

let gateway: PMTGatewayAssetHub | null = null;
let currentPayment: PaymentIntent | null = null;
// let connectedAddress: string | null = null; // Not currently used

// Default merchant address for demo (can be changed in UI)
const DEFAULT_MERCHANT = '5GrwvaEF5zXb26Fz9rcQpDWS57CtERHpNehXCPcNoHGKutQY';

// ============================================================================
// Logging
// ============================================================================

function log(message: string, type: 'success' | 'error' | 'info' = 'info') {
  const logsDiv = document.getElementById('logs')!;
  const timestamp = new Date().toLocaleTimeString();
  const className = `log-${type}`;

  const entry = document.createElement('div');
  entry.className = `log-entry ${className}`;
  entry.textContent = `[${timestamp}] ${message}`;

  logsDiv.appendChild(entry);
  logsDiv.scrollTop = logsDiv.scrollHeight;

  console.log(`[${type.toUpperCase()}] ${message}`);
}

// ============================================================================
// SDK Initialization
// ============================================================================

async function initializeSDK() {
  try {
    log('Initializing PMT Gateway SDK...', 'info');

    gateway = new PMTGatewayAssetHub({
      assetHub: {
        network: 'paseo',
        rpcEndpoint: 'wss://sys.ibp.network/asset-hub-paseo',
      },
    });

    await gateway.initialize();

    log('✅ SDK initialized successfully!', 'success');
    updateConnectionStatus(true);

    (document.getElementById('connect-wallet') as HTMLButtonElement).disabled = false;

    log('Ready to connect wallet', 'success');
  } catch (error) {
    log(`❌ Failed to initialize SDK: ${error}`, 'error');
    updateConnectionStatus(false);
  }
}

// ============================================================================
// Wallet Connection
// ============================================================================

async function connectWallet() {
  if (!gateway) {
    log('SDK not initialized', 'error');
    return;
  }

  try {
    log('Requesting wallet access...', 'info');

    const extensions = await web3Enable('PMT Gateway Checkout');

    if (extensions.length === 0) {
      log('❌ No wallet extension found', 'error');
      alert('Please install Polkadot.js Extension\n\nDownload from:\nhttps://polkadot.js.org/extension/');
      return;
    }

    // Filter to only show Polkadot.js Extension
    const polkadotExtensions = extensions.filter(ext => ext.name === 'polkadot-js');

    if (polkadotExtensions.length === 0) {
      log('❌ Polkadot.js Extension not found', 'error');
      alert('Please install Polkadot.js Extension\n\nDownload from:\nhttps://polkadot.js.org/extension/');
      return;
    }

    log(`✅ Found Polkadot.js Extension`, 'success');

    const allAccounts = await web3Accounts();
    console.log('🔍 All accounts:', allAccounts);

    // Filter out Ethereum accounts (20 bytes, starting with 0x)
    // Only show Substrate/Polkadot accounts (32 bytes)
    const substrateAccounts = allAccounts.filter(account => {
      const isEthereumAddress = account.address.length === 42 && account.address.startsWith('0x');
      if (isEthereumAddress) {
        console.log(`⚠️ Filtering out Ethereum account: ${account.address} (${account.meta.name})`);
      }
      return !isEthereumAddress;
    });

    log(`Found ${allAccounts.length} total accounts (${substrateAccounts.length} Substrate, ${allAccounts.length - substrateAccounts.length} Ethereum)`, 'info');

    if (substrateAccounts.length === 0) {
      log('❌ No Substrate/Polkadot accounts found. Please create a Polkadot account (not Ethereum) in your wallet.', 'error');
      alert('No Substrate/Polkadot accounts found!\n\nYou have Ethereum accounts in your wallet, but this app requires Polkadot accounts.\n\nPlease:\n1. Open Polkadot.js Extension\n2. Click the + button\n3. Select "Create new account"\n4. Follow the prompts to create a Substrate account');
      return;
    }

    showWalletSelector(polkadotExtensions, substrateAccounts);
  } catch (error) {
    log(`❌ Wallet connection failed: ${error}`, 'error');
    console.error('Full error:', error);
  }
}

function showWalletSelector(_extensions: unknown[], allAccounts: unknown[]) {
  // Since we only support Polkadot.js Extension now, skip wallet selection
  // and go straight to account selection
  const connectBtn = document.getElementById('connect-wallet') as HTMLButtonElement;
  connectBtn.style.display = 'none';

  (window as any).availableAccounts = allAccounts;

  // Go directly to account selection for polkadot-js
  showAccountSelector('polkadot-js');
}

function showAccountSelector(walletName: string) {
  const accounts = (window as any).availableAccounts.filter((acc: any) => acc.meta.source === walletName);
  const walletInfo = document.getElementById('wallet-info')!;

  if (accounts.length === 0) {
    log(`❌ No accounts found in ${walletName}`, 'error');
    return;
  }

  let html = `
    <div style="margin-bottom: 16px;">
      <button onclick="window.backToWalletSelector()" style="
        background: #6b7280;
        padding: 6px 12px;
        font-size: 12px;
        margin-bottom: 8px;
        width: auto;
      ">← Back</button>
      <div><strong>Choose an account from ${walletName}:</strong></div>
    </div>
  `;

  accounts.forEach((account: any) => {
    const shortAddr = `${account.address.slice(0, 8)}...${account.address.slice(-8)}`;
    html += `
      <div class="account-selector-item" onclick="window.selectAccount('${account.address}', '${walletName}')" style="
        padding: 12px;
        border: 2px solid #e5e7eb;
        border-radius: 8px;
        margin-bottom: 8px;
        cursor: pointer;
        transition: all 0.2s;
      " onmouseover="this.style.borderColor='#667eea'" onmouseout="this.style.borderColor='#e5e7eb'">
        <div style="font-weight: 600; color: #1f2937; margin-bottom: 4px;">
          ${account.meta.name || 'Unnamed Account'}
        </div>
        <div style="font-size: 12px; color: #6b7280; font-family: 'Monaco', monospace;">
          ${shortAddr}
        </div>
      </div>
    `;
  });

  walletInfo.innerHTML = html;
}

(window as any).backToWalletSelector = async function() {
  const extensions = await web3Enable('PMT Gateway Checkout');
  const allAccounts = await web3Accounts();
  showWalletSelector(extensions, allAccounts);
};

(window as any).selectWallet = function(walletName: string) {
  log(`Selected wallet: ${walletName}`, 'info');
  showAccountSelector(walletName);
};

(window as any).selectAccount = async function(address: string, walletName: string) {
  const accounts = (window as any).availableAccounts;
  const account = accounts.find((acc: any) => acc.address === address);

  if (!account) {
    log('❌ Account not found', 'error');
    return;
  }

  // connectedAddress = account.address; // Tracking connected address (not currently used)

  log(`✅ Selected account: ${account.meta.name || 'Unnamed'}`, 'success');
  log(`Wallet: ${walletName}`, 'info');
  log(`Address: ${account.address}`, 'info');

  const balance = await gateway!.getBalance(account.address);
  const balancePAS = (Number(balance.free) / 1e10).toFixed(4);

  updateWalletInfo(account.address, balancePAS, walletName, account.meta.name || 'Unnamed Account');

  (document.getElementById('create-payment') as HTMLButtonElement).disabled = false;

  log(`Balance: ${balancePAS} PAS`, 'success');

  setupPaymentEventListeners();
};

(window as any).disconnectWallet = function() {
  log('Disconnecting wallet...', 'info');

  // connectedAddress = null; // Reset tracked address
  (window as any).availableAccounts = null;
  currentPayment = null;

  const walletInfo = document.getElementById('wallet-info')!;
  walletInfo.innerHTML = `
    <div class="info-row">
      <span class="label">Status</span>
      <span class="value">Not Connected</span>
    </div>
    <div class="info-row">
      <span class="label">Address</span>
      <span class="value">-</span>
    </div>
    <div class="info-row">
      <span class="label">Balance</span>
      <span class="value">-</span>
    </div>
  `;

  const connectBtn = document.getElementById('connect-wallet') as HTMLButtonElement;
  connectBtn.style.display = 'block';
  connectBtn.disabled = false;

  (document.getElementById('create-payment') as HTMLButtonElement).disabled = true;

  updatePaymentStatus(null);

  log('✅ Wallet disconnected', 'success');
};

// ============================================================================
// Payment Creation & Execution
// ============================================================================

async function createPayment() {
  if (!gateway) return;

  const merchantAddress = (document.getElementById('merchant-address') as HTMLInputElement).value.trim();
  const amount = (document.getElementById('amount') as HTMLInputElement).value.trim();
  const description = (document.getElementById('description') as HTMLInputElement).value.trim();

  if (!merchantAddress || !amount) {
    alert('Please fill in merchant address and amount');
    return;
  }

  try {
    log('Creating payment...', 'info');

    const payment = await gateway.createPayment({
      merchant: merchantAddress,
      amount,
      description: description || 'Payment',
    });

    currentPayment = payment;

    log(`✅ Payment created!`, 'success');
    log(`Payment ID: ${payment.id}`, 'info');
    log(`Amount: ${amount} PAS`, 'info');

    updatePaymentStatus(payment);

  } catch (error) {
    log(`❌ Payment creation failed: ${error}`, 'error');
  }
}

async function executePayment() {
  if (!gateway || !currentPayment) return;

  try {
    log(`Executing payment ${currentPayment.id}...`, 'info');

    await gateway.executePayment(currentPayment.id);

    log(`✅ Payment executed successfully!`, 'success');

    const updatedPayment = gateway.getPayment(currentPayment.id);
    currentPayment = updatedPayment || null;

    updatePaymentStatus(currentPayment);

  } catch (error) {
    log(`❌ Payment execution failed: ${error}`, 'error');
  }
}

async function cancelPayment() {
  if (!gateway || !currentPayment) return;

  try {
    log(`Cancelling payment ${currentPayment.id}...`, 'info');

    await gateway.cancelPayment(currentPayment.id);

    log(`✅ Payment cancelled`, 'info');

    const updatedPayment = gateway.getPayment(currentPayment.id);
    currentPayment = updatedPayment || null;

    updatePaymentStatus(currentPayment);

  } catch (error) {
    log(`❌ Payment cancellation failed: ${error}`, 'error');
  }
}

// ============================================================================
// Event Listeners
// ============================================================================

function setupPaymentEventListeners() {
  if (!gateway) return;

  gateway.on('payment.created', (event) => {
    log(`📋 Payment created: ${event.paymentId}`, 'success');
  });

  gateway.on('payment.completed', async (event) => {
    log(`✅ Payment completed: ${event.paymentId}`, 'success');
    log(`Transaction: ${event.txHash}`, 'info');

    // Verify payment on-chain
    log('🔍 Verifying payment on-chain...', 'info');
    try {
      const verification = await gateway!.verifyPayment(event.paymentId);

      if (verification.verified) {
        log('✅ Payment verified on-chain!', 'success');
        log(`Amount verified: ${(Number(verification.amount) / 1e10).toFixed(4)} PAS`, 'success');
        log(`Block number: ${verification.blockNumber}`, 'info');
        log(`From: ${verification.from.slice(0, 8)}...${verification.from.slice(-8)}`, 'info');
        log(`To: ${verification.to.slice(0, 8)}...${verification.to.slice(-8)}`, 'info');

        // Safe to deliver product/service here
        log('✅ Safe to deliver product/service', 'success');
      } else {
        log(`❌ Payment verification failed: ${verification.error}`, 'error');
        log('⚠️ DO NOT deliver product/service', 'error');
      }
    } catch (error) {
      log(`❌ Verification error: ${error}`, 'error');
    }
  });

  gateway.on('payment.cancelled', (event) => {
    log(`↩️ Payment cancelled: ${event.paymentId}`, 'info');
  });
}

// ============================================================================
// UI Updates
// ============================================================================

function updateConnectionStatus(connected: boolean) {
  const statusEl = document.getElementById('connection-status')!;
  statusEl.textContent = connected ? '✓ Connected to Paseo' : 'Connecting...';
  statusEl.className = `status ${connected ? 'connected' : 'disconnected'}`;
}

function updateWalletInfo(address: string, balance: string, walletName: string, accountName: string) {
  const walletInfo = document.getElementById('wallet-info')!;
  const connectBtn = document.getElementById('connect-wallet') as HTMLButtonElement;

  connectBtn.style.display = 'none';

  walletInfo.innerHTML = `
    <div class="info-row">
      <span class="label">Wallet</span>
      <span class="value" style="color: #10b981; font-weight: 700;">✓ ${walletName}</span>
    </div>
    <div class="info-row">
      <span class="label">Account</span>
      <span class="value">${accountName}</span>
    </div>
    <div class="info-row">
      <span class="label">Address</span>
      <span class="value" style="font-family: 'Monaco', monospace; font-size: 11px;">${address.slice(0, 8)}...${address.slice(-8)}</span>
    </div>
    <div class="info-row">
      <span class="label">Balance</span>
      <span class="value" style="font-weight: 700;">${balance} PAS</span>
    </div>
    <button onclick="window.disconnectWallet()" style="background: #ef4444; margin-top: 4px;">Disconnect</button>
  `;
}

function updatePaymentStatus(payment: PaymentIntent | null) {
  const statusDiv = document.getElementById('payment-status')!;
  const statusCard = document.getElementById('payment-status-card')!;

  if (!payment) {
    statusCard.style.display = 'none';
    return;
  }

  statusCard.style.display = 'block';
  const amount = (Number(payment.amount) / 1e10).toFixed(4);

  statusDiv.innerHTML = `
    <div style="background: #f9fafb; border: 2px solid #e5e7eb; border-radius: 8px; padding: 16px;">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
        <span style="font-weight: 600; font-size: 13px;">Payment ID:</span>
        <span style="font-family: 'Monaco', monospace; font-size: 10px;">${payment.id.slice(0, 16)}...</span>
      </div>
      <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
        <span>Amount:</span>
        <span style="font-weight: 700;">${amount} PAS</span>
      </div>
      <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
        <span>Status:</span>
        <span style="text-transform: uppercase; font-weight: 600; color: ${
          payment.status === 'completed' ? '#10b981' :
          payment.status === 'cancelled' ? '#ef4444' :
          '#667eea'
        };">${payment.status}</span>
      </div>
      ${payment.txHash ? `
        <div style="margin-top: 12px; padding-top: 12px; border-top: 1px solid #e5e7eb;">
          <span style="font-size: 11px; color: #6b7280;">Transaction:</span>
          <div style="font-family: 'Monaco', monospace; font-size: 10px; word-break: break-all; margin-top: 4px;">
            ${payment.txHash}
          </div>
        </div>
      ` : ''}
      ${payment.status === 'created' ? `
        <div style="display: flex; gap: 8px; margin-top: 16px;">
          <button onclick="window.executePayment()" style="background: #10b981; flex: 1;">Execute Payment</button>
          <button onclick="window.cancelPayment()" style="background: #ef4444; flex: 1;">Cancel</button>
        </div>
      ` : ''}
    </div>
  `;
}

// ============================================================================
// Event Handlers
// ============================================================================

document.getElementById('connect-wallet')?.addEventListener('click', connectWallet);
document.getElementById('create-payment')?.addEventListener('click', createPayment);

// Expose functions to window
(window as any).executePayment = executePayment;
(window as any).cancelPayment = cancelPayment;

// ============================================================================
// Initialize on Load
// ============================================================================

window.addEventListener('DOMContentLoaded', () => {
  log('PMT Gateway Checkout Demo Starting...', 'info');

  // Pre-fill merchant address
  (document.getElementById('merchant-address') as HTMLInputElement).value = DEFAULT_MERCHANT;

  initializeSDK();
});
